/**
 * Booking breakdown estimation
 *
 * Transactions have payment information that can be shown with the
 * BookingBreakdown component. However, when selecting booking
 * details, there is no transaction object present and we have to
 * estimate the breakdown of the transaction without data from the
 * API.
 *
 * If the payment process of a customized marketplace is something
 * else than simply daily or nightly bookings, the estimation will
 * most likely need some changes.
 *
 * To customize the estimation, first change the BookingDatesForm to
 * collect all booking information from the user (in addition to the
 * default date pickers), and provide that data to the
 * EstimatedBreakdownMaybe components. You can then make customization
 * within this file to create a fake transaction object that
 * calculates the breakdown information correctly according to the
 * process.
 *
 * In the future, the optimal scenario would be to use the same
 * transactions.initiateSpeculative API endpoint as the CheckoutPage
 * is using to get the breakdown information from the API, but
 * currently the API doesn't support that for logged out users, and we
 * are forced to estimate the information here.
 */
import React from 'react';
import Decimal from 'decimal.js';
import { path, pathOr } from 'ramda';

import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LINE_ITEM_BARBECUE_FEE,
  LINE_ITEM_TOWELS_FEE,
  LINE_ITEM_SNACK_FEE,
  LINE_ITEM_JACUZZI_FEE,
  LINE_ITEM_UNITS1,
  LINE_ITEM_UNITS2,
  LINE_ITEM_UNITS3,
  LINE_ITEM_IS_BOOKING_FULL_DAY,
} from '../../util/types';
import { TRANSITION_REQUEST_PAYMENT, TX_TRANSITION_ACTOR_CUSTOMER } from '../../util/transactions';
import { unitDivisor, convertMoneyToNumber, convertUnitToSubUnit } from '../../util/currency';
import { addMultipleMoney } from '../../util/swimmy';
import { BookingBreakdown } from '../../components';
import config from '../../config';
import css from './BookingDatesForm.css';
import { getDisplayDates } from '../../util/dates';

const { Money, UUID } = sdkTypes;

const estimatedUnitsTotal  = (unitPrice, unitCount) => {
  const numericPrice = convertMoneyToNumber(unitPrice);
  const numericTotalPrice = new Decimal(numericPrice).times(unitCount).toNumber();
  return new Money(
    convertUnitToSubUnit(numericTotalPrice, unitDivisor(unitPrice.currency)),
    unitPrice.currency,
  );
};

const estimatedDiscountedUnitPrice = (unitPrice, discountMultiplier) => {
  const numericPrice = convertMoneyToNumber(unitPrice);
  const numericDiscountedUnitPrice = new Decimal(numericPrice)
    .times(discountMultiplier)
    .toDP(2)
    .toNumber();
  return new Money(
    convertUnitToSubUnit(numericDiscountedUnitPrice, unitDivisor(unitPrice.currency)),
    unitPrice.currency,
  );
};

const estimatedCustomerCommission = (unitsTotal, percentage) => {
  const numericTotal = convertMoneyToNumber(unitsTotal);
  // Divide the commission percentage with 100 to get the
  // commission multiplier and use precision of 2 when
  // rounding the sub units.
  const numericCommission = new Decimal(percentage)
    .div(100)
    .times(numericTotal)
    .toDP(2)
    .toNumber();

  return new Money(
    convertUnitToSubUnit(numericCommission, unitDivisor(unitsTotal.currency)),
    unitsTotal.currency,
  );
};

const estimatedPayInTotal = (unitsTotal, customerCommission) => {
  const numericTotal = convertMoneyToNumber(unitsTotal);
  const numericCommission = convertMoneyToNumber(customerCommission);
  const numericPayIn = new Decimal(numericTotal).plus(numericCommission).toNumber();
  return new Money(
    convertUnitToSubUnit(numericPayIn, unitDivisor(unitsTotal.currency)),
    unitsTotal.currency,
  );
};

// When we cannot speculatively initiate a transaction (i.e. logged
// out), we must estimate the booking breakdown. This function creates
// an estimated transaction object for that use case.
const estimatedTransaction = (items = {}) => {
  const {
    isBookingFullDay,
    startDate: bookingStart,
    endDate: bookingEnd,
    unitPrice,
    quantity1,
    quantity2,
    quantity3,
    barbecueFee,
    towelsFee,
    snackFee,
    jacuzziFee,
    listing,
  } = items;
  const now = new Date();

  const fullDayPriceData = pathOr(null, ['attributes', 'publicData', 'fullDayPrice'], listing);
  const fullDayPriceAmount = fullDayPriceData ? fullDayPriceData : unitPrice.amount * 2;
  const unitPriceMaybe = isBookingFullDay && unitPrice ? new Money(fullDayPriceAmount, unitPrice.currency) : unitPrice;
  const adultsTotal = estimatedUnitsTotal(unitPriceMaybe, quantity1);

  const childrenUnitPrice = estimatedDiscountedUnitPrice(
    unitPriceMaybe,
    config.custom.childrenDiscountMultiplier,
  );
  const infantsUnitPrice = new Money(0, unitPrice.currency);
  const numberOfPeople = quantity1 + quantity2;
  const childrenTotal = estimatedUnitsTotal(childrenUnitPrice, quantity2);
  const infantsTotal = new Money(0);
  const barbecueFeeTotal = barbecueFee ? estimatedUnitsTotal(barbecueFee, 1) : new Money(0);
  const towelsFeeTotal = towelsFee ? estimatedUnitsTotal(towelsFee, numberOfPeople) : new Money(0);
  const snackFeeTotal = snackFee ? estimatedUnitsTotal(snackFee, numberOfPeople) : new Money(0);
  const jacuzziFeeTotal = jacuzziFee ? estimatedUnitsTotal(jacuzziFee, numberOfPeople) : new Money(0);
  const unitsTotal = addMultipleMoney(
    adultsTotal,
    childrenTotal,
    infantsTotal,
    barbecueFeeTotal,
    towelsFeeTotal,
    snackFeeTotal,
    jacuzziFeeTotal,
  );
  const customerCommission = estimatedCustomerCommission(
    unitsTotal,
    config.custom.customerCommissionPercentage,
  );
  const payInTotal = estimatedPayInTotal(unitsTotal, customerCommission);
  const barbecueFeePrice = barbecueFee && new Money(
    convertUnitToSubUnit(convertMoneyToNumber(barbecueFee), unitDivisor(unitPrice.currency)),
    unitPrice.currency,
  );
  const jacuzziFeePrice = jacuzziFee && new Money(
    convertUnitToSubUnit(convertMoneyToNumber(jacuzziFee), unitDivisor(unitPrice.currency)),
    unitPrice.currency,
  );
  const snackFeePrice = snackFee && new Money(
    convertUnitToSubUnit(convertMoneyToNumber(snackFee), unitDivisor(unitPrice.currency)),
    unitPrice.currency,
  );
  const towelsFeePrice = towelsFee && new Money(
    convertUnitToSubUnit(convertMoneyToNumber(towelsFee), unitDivisor(unitPrice.currency)),
    unitPrice.currency,
  );
  const barbecueFeeLineItem = {
    code: LINE_ITEM_BARBECUE_FEE,
    includeFor: ['customer', 'provider'],
    unitPrice: barbecueFeePrice,
    quantity: new Decimal(1),
    lineTotal: barbecueFeeTotal,
    reversal: false,
  };
  const towelsFeeLineItem = {
    code: LINE_ITEM_TOWELS_FEE,
    includeFor: ['customer', 'provider'],
    unitPrice: towelsFeePrice,
    quantity: numberOfPeople,
    lineTotal: towelsFeeTotal,
    reversal: false,
  };
  const snackFeeLineItem = {
    code: LINE_ITEM_SNACK_FEE,
    includeFor: ['customer', 'provider'],
    unitPrice: snackFeePrice,
    quantity: numberOfPeople,
    lineTotal: snackFeeTotal,
    reversal: false,
  };
  const jacuzziFeeLineItem = {
    code: LINE_ITEM_JACUZZI_FEE,
    includeFor: ['customer', 'provider'],
    unitPrice: jacuzziFeePrice,
    quantity: numberOfPeople,
    lineTotal: jacuzziFeeTotal,
    reversal: false,
  };
  const barbecueFeeLineItemMaybe = barbecueFee
    ? [barbecueFeeLineItem]
    : [];
  const towelsFeeLineItemMaybe = towelsFee
    ? [towelsFeeLineItem]
    : [];
  const snackFeeLineItemMaybe = snackFee
    ? [snackFeeLineItem]
    : [];
  const jacuzziFeeLineItemMaybe = jacuzziFee
    ? [jacuzziFeeLineItem]
    : [];
  const isBookingFullDayLineItemMaybe = isBookingFullDay ? [{
    code: LINE_ITEM_IS_BOOKING_FULL_DAY,
    includeFor: ['customer', 'provider'],
    unitPrice: new Money(0, unitPrice.currency),
    quantity: 0,
    lineTotal: new Money(0, unitPrice.currency),
    reversal: false,
  }] : [];
  return {
    id: new UUID('estimated-transaction'),
    type: 'transaction',
    attributes: {
      processVersion: 2,
      createdAt: now,
      lastTransitionedAt: now,
      lastTransition: TRANSITION_REQUEST_PAYMENT,
      payinTotal: payInTotal,
      payoutTotal: payInTotal,
      lineItems: [
        {
          code: LINE_ITEM_UNITS1,
          includeFor: ['customer', 'provider'],
          unitPrice: unitPriceMaybe,
          quantity: new Decimal(quantity1),
          lineTotal: adultsTotal,
          reversal: false,
          isBookingFullDay,
        },
        {
          code: LINE_ITEM_UNITS2,
          includeFor: ['customer', 'provider'],
          unitPrice: childrenUnitPrice,
          quantity: new Decimal(quantity2),
          lineTotal: childrenTotal,
          reversal: false,
          isBookingFullDay,
        },
        {
          code: LINE_ITEM_UNITS3,
          includeFor: ['customer', 'provider'],
          unitPrice: infantsUnitPrice,
          quantity: new Decimal(quantity3),
          lineTotal: infantsTotal,
          reversal: false,
          isBookingFullDay,
        },
        ...isBookingFullDayLineItemMaybe,
        ...barbecueFeeLineItemMaybe,
        ...towelsFeeLineItemMaybe,
        ...snackFeeLineItemMaybe,
        ...jacuzziFeeLineItemMaybe,
        {
          code: 'line-item/customer-commission',
          includeFor: ['customer'],
          unitPrice: unitsTotal,
          lineTotal: customerCommission,
          reversal: false,
        },
      ],
      transitions: [
        {
          createdAt: now,
          by: TX_TRANSITION_ACTOR_CUSTOMER,
          transition: TRANSITION_REQUEST_PAYMENT,
        },
      ],
    },
    booking: {
      id: new UUID('estimated-booking'),
      type: 'booking',
      attributes: {
        displayStart: bookingStart,
        displayEnd: bookingEnd,
        isEveningMode: true,
      },
    },
  };
};

const EstimatedBreakdownMaybe = props => {
  const { listing } = props;
  const {
    unitType,
    unitPrice,
    isBookingFullDay,
    startDate,
    quantity1,
    quantity2,
    quantity3,
    barbecueFee,
    towelsFee,
    snackFee,
    jacuzziFee,
  } = props.bookingData;
  const quantityIfUsingUnits = Number.isInteger(quantity1) && Number.isInteger(quantity2) && Number.isInteger(quantity3);
  const canEstimatePrice = startDate && unitPrice && quantityIfUsingUnits;
  if (!canEstimatePrice) {
    return null;
  }

  const listingSessionTimes = {
    morningSlot: path(['attributes', 'publicData', 'morningSlot'], listing),
    afternoonSlot: path(['attributes', 'publicData', 'afternoonSlot'], listing),
    eveningSlot: path(['attributes', 'publicData', 'eveningSlot'], listing),
  };
  const { start, end } = getDisplayDates(true, false, {
    start: startDate,
  }, listingSessionTimes, isBookingFullDay);
  const tx = estimatedTransaction({
    isBookingFullDay,
    unitType,
    startDate: start,
    endDate: end,
    unitPrice,
    quantity1,
    quantity2,
    quantity3,
    barbecueFee,
    towelsFee,
    snackFee,
    jacuzziFee,
    listing,
  });

  return (
    <BookingBreakdown
      className={css.receipt}
      userRole="customer"
      unitType={unitType}
      transaction={tx}
      booking={tx.booking}
    />
  );
};

export default EstimatedBreakdownMaybe;



// WEBPACK FOOTER //
// ./src/forms/BookingDatesForm/EstimatedBreakdownMaybe.js